<template>
  <div :class="b()">
    <div :class="b('wrapper')">
      <div
        ref="stage"
        :class="b('stage')"
      >
        <div :class="b('container')">
          <template v-if="images.length">
            <div
              v-for="(image, index) in images"
              :key="image.id"
              :class="b('slide')"
            >
              <e-picture
                :srcset="image.srcset"
                :alt="image?.alt || image?.title || $t('c-product-image-gallery.slide-image', { index })"
                :title="image.title"
                :fallback="image.fallback"
                :ratio="1"
                :sizes="pictureSizes"
                :loading="index === 0 ? 'eager' : 'lazy'"
              />
            </div>
          </template>
          <template v-else>
            <div :class="b('slide')">
              <e-picture
                :alt="$t('c-product-image-gallery.no-image')"
                placeholder
                :ratio="1"
                :sizes="pictureSizes"
                loading="lazy"
              />
            </div>
          </template>
        </div>

        <button
          v-if="isLightboxAvailable"
          :class="b('zoom-button')"
          :aria-label="$t('c-product-image-gallery.button-zoom-aria')"
          type="button"
          @click="showLightbox = true"
        >
          <e-icon
            icon="i-zoom-in"
            size="20"
            aria-hidden="true"
          />
        </button>

        <slot></slot>
      </div>

      <div
        v-if="hasThumbs"
        ref="thumbs"
        :class="b('thumbnails')"
      >
        <div :class="b('thumbnails-container')">
          <div
            v-for="(image, index) in images"
            :key="image.id"
            :class="b('thumbnail-slide', { active: activeSlide === index })"
            :aria-label="$t('c-product-image-gallery.thumbnail-image-aria', { index: index + 1 })"
            @click="scrollTo(index)"
          >
            <e-picture
              :class="b('thumbnail-image')"
              :srcset="image.srcset"
              :alt="image?.alt || image?.title || $t('c-product-image-gallery.thumbnail-image', { index })"
              :title="image.title"
              :fallback="image.fallback"
              :ratio="1"
              :sizes="pictureSizes"
              :loading="index === 0 ? 'eager' : 'lazy'"
            />
          </div>
        </div>
      </div>

      <div
        v-if="noOfSlides > 1"
        :class="b('footer')"
      >
        <button
          :class="b('button', { prev: true })"
          :aria-label="$t('c-product-image-gallery.button-prev-aria')"
          type="button"
          @click="previous"
        >
          <e-icon
            icon="i-chevron--left"
            size="25"
            aria-hidden="true"
          />
        </button>
        <div :class="b('pagination')">
          <button
            v-for="(image, index) in images"
            :key="image.id"
            :class="b('pagination-button', { active: activeSlide === index })"
            :disabled="activeSlide === index"
            :aria-label="$t('c-product-image-gallery.button-pagination-bullet-aria', { index: index + 1 })"
            type="button"
            @click="scrollTo(index)"
          >
            <span :class="b('pagination-button-label')"></span>
          </button>
        </div>
        <button
          :class="b('button', { next: true })"
          :aria-label="$t('c-product-image-gallery.button-next-aria')"
          type="button"
          @click="next"
        >
          <e-icon
            icon="i-chevron--right"
            size="25"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <c-lightbox
      v-if="isLightboxAvailable"
      v-model:is-open="showLightbox"
      @next="next"
      @previous="previous"
    >
      <figure
        v-if="showLightbox && activeSlideImage"
        :key="activeSlide"
      >
        <e-picture
          :srcset="activeSlideImage.srcset"
          :alt="activeSlideImage?.alt || activeSlideImage?.title || $t('c-product-image-gallery.lightbox-image')"
          :title="activeSlideImage.title"
          :fallback="activeSlideImage.fallback"
          :ratio="1"
          :sizes="lightboxPictureSizes"
          :placeholder="false"
          loading="eager"
        />
        <figcaption v-if="activeSlideImage.title">
          {{ activeSlideImage.title }}
        </figcaption>
      </figure>
    </c-lightbox>
  </div>
</template>

<script lang="ts">
  import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel';
  import { PropType, Ref, defineComponent, ref } from 'vue';
  import { DEFAULT_IMAGE_SIZES, PRODUCT_DISPLAY_PICTURE_SIZES } from '@/setup/globals';
  import cLightbox from '@/components/c-lightbox.vue';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';
  import { Image } from '@/types/image';
  import { ImageSizes } from '@/types/sizes';

  type Setup = {
    stage: Ref<HTMLDivElement | null | undefined>;
    thumbs: Ref<HTMLDivElement | null | undefined>;
  };

  type Data = {
    pictureSizes: ImageSizes;
    lightboxPictureSizes: ImageSizes;
    emblaInstance: null | EmblaCarouselType;
    emblaInstanceThumbs: null | EmblaCarouselType;
    activeSlide: number;
    showLightbox: boolean;
  };

  /**
   * Renders the product image gallery.
   */
  export default defineComponent({
    name: 'c-product-image-gallery',
    components: {
      ePicture,
      eIcon,
      cLightbox,
    },

    props: {
      /**
       * Expects a list of images.
       */
      images: {
        type: Array as PropType<Image[]>,
        required: true,
      },

      /**
       * Define if thumbs should be shown.
       */
      useThumbs: {
        type: Boolean,
        default: false,
      },

      /**
       * Define if the lightbox should be available.
       */
      useLightbox: {
        type: Boolean,
        default: false,
      },
    },
    // emits: {},

    setup(): Setup {
      return {
        stage: ref(),
        thumbs: ref(),
      };
    },
    data(): Data {
      return {
        pictureSizes: PRODUCT_DISPLAY_PICTURE_SIZES,
        lightboxPictureSizes: DEFAULT_IMAGE_SIZES,
        emblaInstance: null,
        emblaInstanceThumbs: null,
        activeSlide: 0,
        showLightbox: false,
      };
    },

    computed: {
      /**
       * Returns the number of slides.
       */
      noOfSlides(): number {
        return this.images?.length || 0;
      },

      /**
       * Returns the image of the currently active slide.
       */
      activeSlideImage(): Image | null {
        return this.images[this.activeSlide] || null;
      },

      /**
       * Returns if thumbnails are available.
       */
      hasThumbs(): boolean {
        return this.useThumbs && this.noOfSlides > 1;
      },

      /**
       * Returns if the lightbox can be shown.
       */
      isLightboxAvailable(): boolean {
        return this.useLightbox && this.$viewport.isLg && !!this.images?.length;
      },
    },
    watch: {
      /**
       * Observes change of `activeSlide` from lightbox
       * and updates carousel.
       */
      activeSlide(index): void {
        if (!this.emblaInstance) {
          return;
        }

        const thumbnailsElement = document.querySelector(`.${this.b('thumbnails')}`);
        const thumbnailSlide = document.querySelector(`.${this.b('thumbnail-slide')}`);

        if (thumbnailsElement && thumbnailSlide) {
          thumbnailsElement.scrollTo({
            left: index * (thumbnailSlide.getBoundingClientRect().width + 10),
            behavior: 'smooth',
          });
        }

        const footerElement = document.querySelector(`.${this.b('pagination')}`);
        const paginationButton = document.querySelector(`.${this.b('pagination-button')}`);

        if (footerElement && paginationButton) {
          footerElement.scrollTo({
            left: index * (paginationButton.getBoundingClientRect().width + 8),
            behavior: 'smooth',
          });
        }

        if (index !== this.emblaInstance.selectedScrollSnap()) {
          this.scrollTo(index);
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      if (this.images.length) {
        this.emblaInstance = EmblaCarousel(this.stage as HTMLElement, { skipSnaps: false }); // eslint-disable-line new-cap
        this.emblaInstance.on('select', this.onSelectByMain);
      }

      if (this.images.length && this.hasThumbs) {
        this.emblaInstanceThumbs = EmblaCarousel(this.thumbs as HTMLElement, { skipSnaps: false }); // eslint-disable-line new-cap
        this.emblaInstanceThumbs.on('select', this.onSelectByThumb);
      }
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.emblaInstance?.destroy();
      this.emblaInstanceThumbs?.destroy();
    },
    // unmounted() {},

    methods: {
      /**
       * Event handler for the sliders select event.
       * Updates the slides active classes
       */
      onSelectByMain(): void {
        if (this.emblaInstance) {
          this.activeSlide = this.emblaInstance.selectedScrollSnap();
        }
      },

      /**
       * Event handler for the sliders select event.
       * Updates the slides active classes
       */
      onSelectByThumb(): void {
        if (this.emblaInstanceThumbs) {
          this.activeSlide = this.emblaInstanceThumbs.selectedScrollSnap();
        }
      },

      /**
       * Scrolls to the slide with the given index.
       */
      scrollTo(index: number): void {
        this.emblaInstance?.scrollTo(index);
        this.emblaInstanceThumbs?.scrollTo(index);
      },

      /**
       * Scrolls to the next slide.
       */
      next(): void {
        this.scrollTo(this.activeSlide === this.noOfSlides - 1 ? 0 : this.activeSlide + 1);
      },

      /**
       * Scrolls to the previous slide.
       */
      previous(): void {
        this.scrollTo(this.activeSlide === 0 ? this.noOfSlides - 1 : this.activeSlide - 1);
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';

  .c-product-image-gallery {
    margin-bottom: 2rem;

    &__wrapper {
      position: relative;
    }

    &__stage {
      overflow: hidden;
      width: 100%;
      position: relative;
      margin-bottom: 2rem;
    }

    &__container {
      display: flex;
      user-select: none;
    }

    &__slide {
      min-width: 100%;
      overflow: hidden;
      position: relative;
    }

    &__lightbox-slide {
      min-width: 100%;
      overflow: hidden;
    }

    &__zoom-button {
      @include mixins.hover-effect--default($transition: true);

      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
      padding: 0.5rem;
      border-radius: 100%;
      cursor: pointer;
      color: variables.$va-color-grayscale--1000;
      background-color: variables.$va-color-grayscale--0;
      box-shadow: 0 0 15px rgba(var(--theme-color-grayscale--1000-rgb), 0.4);
      z-index: 2;
    }

    &__button--prev,
    &__button--next {
      cursor: pointer;

      .e-icon {
        @include mixins.media(lg) {
          width: 18px;
          height: 18px;
        }
      }
    }

    &__button:not([disabled]) {
      @include mixins.hover-effect--default($transition: true);
    }

    &__button[disabled] {
      opacity: 0.25;
      cursor: default;
    }

    &__footer {
      display: flex;
      justify-content: center;
      align-self: center;
    }

    &__pagination {
      @include mixins.hide-scrollbars;

      display: flex;
      align-self: center;
      margin: 0 1rem;
      max-width: 90%;
      flex-flow: row nowrap;
      gap: 0.5rem;
      justify-content: flex-start;
      overflow: scroll;
    }

    &__pagination-button {
      flex-shrink: 0;
      width: 0.75rem;
      height: 0.75rem;
      background-color: variables.$va-color-grayscale--1000;
      border-radius: 100%;
      border: 2px solid variables.$va-color-grayscale--700;
      cursor: pointer;

      &--active {
        background-color: variables.$va-color-grayscale--0;
        border-color: variables.$va-color-grayscale--0;
      }

      &:not(&--active) {
        @include mixins.hover-effect--default($transition: true);
      }

      &[disabled] {
        cursor: default;
      }
    }

    &__pagination-button-label {
      display: none;
    }

    &__thumbnails {
      @include mixins.hide-scrollbars;

      overflow: scroll;
      display: flex;
      margin-bottom: 1.5rem;
    }

    &__thumbnails-container {
      display: flex;
      user-select: none;
      gap: 10px;
    }

    &__thumbnail-slide {
      flex: 0 0 var(--theme-image-size--sm);
      min-width: 0;
      padding-bottom: 1rem;
      border-bottom: 2px solid transparent;
      cursor: pointer;

      &--active {
        border-color: variables.$va-color-grayscale--700;
      }

      &:not(&--active) {
        @include mixins.hover-effect--default($transition: true);
      }

      &:hover:not(&--active) {
        border-color: variables.$va-color-grayscale--900;
      }
    }
  }
</style>
